// services/school-ui/src/pages/chat/attachments/TeacherAttachmentChip.tsx
//
// Single attachment chip. Used in two surfaces:
//   1. Composer — wrapped by ComposerPrimitive.Attachments. Renders
//      progress / ready / error states via AttachmentPrimitive.Root's
//      context; remove button via AttachmentPrimitive.Remove.
//   2. Message history — same component, but the attachment carries a
//      `data-unavailable` marker if the kernel returned 410 on the
//      attachment_id. Renders disabled with the "unavailable" copy.
//
// The library exposes AttachmentPrimitive.{Root,Name,Remove,Thumb};
// this component supplies the teacher workspace chip treatment (progress /
// ready / error / unavailable states + retry + remove).

import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AttachmentPrimitive,
  useAttachment,
  useAttachmentRuntime,
  useComposerRuntime,
} from "@assistant-ui/react";

// The manual "↗ save to my library" affordance was removed 2026-07-16:
// chat uploads now auto-persist into 我的文件 at upload time (see
// ConversationAttachmentAdapter → saveFileToMyFiles), so a per-chip save
// button was redundant and, because it targeted the artifacts/ subfolder,
// produced a confusing duplicate. Deletion/management lives on the
// /documents page.
export function TeacherAttachmentChip(): JSX.Element {
  const { t } = useTranslation();
  const attachmentState = useAttachment();
  const attachmentRuntime = useAttachmentRuntime();
  const composer = useComposerRuntime();
  const status = attachmentState.status;
  const isRunning = status.type === "running";
  const isError = status.type === "incomplete" && status.reason === "error";
  // Message-source chips render on an already-sent user message (via
  // MessagePrimitive.Attachments): they are immutable (no remove) and
  // carry no transient "ready/uploading" state to show.
  const isMessage = attachmentState.source === "message";
  const unavailable =
    // history-loaded chips with a deleted_at marker thread it via
    // the `unavailable` flag on the attachment metadata. The
    // AttachmentPrimitive.Root context surfaces all attachment
    // fields including unknown ones; we read it lazily.
    Boolean((attachmentState as { unavailable?: boolean }).unavailable);

  function handleRetry() {
    // Retry semantics per spec acceptance item 6: remove the errored
    // chip + re-pick the same file. The original File reference is
    // preserved on the PendingAttachment by the library (we set it in
    // ConversationAttachmentAdapter.add). attachmentRuntime.remove()
    // removes the chip; composer.addAttachment() re-uploads it.
    // Only composer attachments can be removed; message attachments
    // have remove() returning never (they cannot be removed).
    if (!attachmentState.file) return;
    if (attachmentState.source === "message") return;
    const file = attachmentState.file;
    void attachmentRuntime.remove().then(() => {
      void composer.addAttachment(file);
    });
  }

  const className = [
    "teacher-attachment-chip",
    isRunning ? "is-uploading" : "",
    isError ? "is-error" : "",
    unavailable ? "is-unavailable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // 图片附件显示真缩略图(composer 阶段持有 File);其余类型保留扩展名徽标。
  const file = attachmentState.file;
  const thumbUrl = useMemo(
    () => (file && file.type.startsWith("image/") ? URL.createObjectURL(file) : null),
    [file],
  );
  useEffect(() => () => {
    if (thumbUrl) URL.revokeObjectURL(thumbUrl);
  }, [thumbUrl]);

  return (
    <AttachmentPrimitive.Root className={className}>
      {thumbUrl ? (
        // 解码失败(损坏文件/伪 mime)时隐藏,退回默认徽标行为
        <img
          className="thumb-img"
          src={thumbUrl}
          alt=""
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <AttachmentPrimitive.unstable_Thumb />
      )}
      <div className="meta">
        <div className="name">
          <AttachmentPrimitive.Name />
        </div>
        {(!isMessage || unavailable) && (
          <span className="state">
            {isRunning
              ? t("chat.attachments.uploading", {
                  progress: Math.round((status as { progress: number }).progress * 100),
                })
              : isError
                ? t("chat.attachments.errorUnknown")
                : unavailable
                  ? t("chat.attachments.history.unavailable")
                  : t("chat.attachments.ready")}
          </span>
        )}
      </div>
      {isError && attachmentState.file && attachmentState.source !== "message" && (
        <button
          type="button"
          className="retry"
          aria-label={t("chat.attachments.retry")}
          onClick={handleRetry}
        >
          ↻
        </button>
      )}
      {!unavailable && !isMessage && (
        <AttachmentPrimitive.Remove
          className="remove"
          aria-label={t("chat.attachments.remove")}
        >
          ×
        </AttachmentPrimitive.Remove>
      )}
    </AttachmentPrimitive.Root>
  );
}
